import React, { useState } from "react";
import { Mail, Phone, MessageCircle, MapPin, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { ScrollFade, HeroStaggerText } from "@/lib/animations";

const SUPPORT_EMAIL = "support.norbucollection@gmail.com";
const SUPPORT_PHONE = "+91 95609 95329";
const WHATSAPP_LINK = "https://wa.me/919560995329?text=Hi%20Norbu%20Collection";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  function update(k, v) { setForm(prev => ({ ...prev, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message");
      return;
    }
    setSending(true);
    // Open user's mail client with prefilled content — works without backend email integration yet
    const body = `Name: ${form.name}%0D%0AEmail: ${form.email}%0D%0A%0D%0A${encodeURIComponent(form.message)}`;
    const subject = encodeURIComponent(form.subject || "Customer enquiry — Norbu Collection");
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    setTimeout(() => {
      setSending(false);
      toast.success("Email opened. We'll reply within 24 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    }, 700);
  }

  return (
    <Layout>
      <section className="bg-[#163A2F] text-[#F8F6F0] grain relative pt-20 pb-24 md:pt-28 md:pb-32">
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="overline text-[#C9A227]">Get in touch</div>
          <HeroStaggerText
            text="We're here. Always."
            className="mt-3 text-5xl md:text-7xl font-extralight tracking-tight"
          />
          <p className="mt-6 max-w-xl text-[#F8F6F0]/75 font-light text-base md:text-lg">
            Question about sizing, an order, a return, or a custom request — write to us, call us, or just say hi on WhatsApp. We reply within 24 hours.
          </p>
        </div>
      </section>

      <section className="bg-[#F8F6F0] py-16 md:py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Channels */}
          <ScrollFade className="lg:col-span-5 space-y-6">
            <div className="overline text-[#C9A227]">Reach us</div>
            <h2 className="text-3xl md:text-4xl font-light text-[#163A2F] tracking-tight">Direct channels</h2>

            <div className="mt-6 space-y-4">
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                data-testid="contact-email-link"
                className="group flex items-start gap-4 border hairline p-5 hover:border-[#C9A227] transition-colors"
              >
                <Mail className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <div className="overline text-[#163A2F]/60">Email</div>
                  <div className="mt-1 text-[#163A2F] font-medium break-all">{SUPPORT_EMAIL}</div>
                  <div className="text-xs text-[#163A2F]/55 mt-0.5">Best for detailed enquiries</div>
                </div>
              </a>

              <a
                href={`tel:${SUPPORT_PHONE.replace(/\s/g, "")}`}
                data-testid="contact-phone-link"
                className="group flex items-start gap-4 border hairline p-5 hover:border-[#C9A227] transition-colors"
              >
                <Phone className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <div className="overline text-[#163A2F]/60">Phone</div>
                  <div className="mt-1 text-[#163A2F] font-medium">{SUPPORT_PHONE}</div>
                  <div className="text-xs text-[#163A2F]/55 mt-0.5">Mon — Sat · 10am — 7pm IST</div>
                </div>
              </a>

              <a
                href={WHATSAPP_LINK}
                target="_blank" rel="noreferrer"
                data-testid="contact-whatsapp-link"
                className="group flex items-start gap-4 border hairline p-5 hover:border-[#C9A227] transition-colors"
              >
                <MessageCircle className="w-6 h-6 text-[#25D366] flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <div className="overline text-[#163A2F]/60">WhatsApp</div>
                  <div className="mt-1 text-[#163A2F] font-medium">Chat with our team</div>
                  <div className="text-xs text-[#163A2F]/55 mt-0.5">Fastest replies, usually under an hour</div>
                </div>
              </a>
            </div>

            <div className="mt-8 border hairline p-5">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <div className="overline text-[#163A2F]/60">Studio</div>
                  <div className="text-[#163A2F] mt-1">Norbu Collection</div>
                  <div className="text-[#163A2F]/70 text-sm">New Delhi · India</div>
                </div>
              </div>
              <div className="mt-4 flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <div className="overline text-[#163A2F]/60">Reply time</div>
                  <div className="text-[#163A2F]/70 text-sm mt-1">Within 24 hours, every day.</div>
                </div>
              </div>
            </div>
          </ScrollFade>

          {/* Form */}
          <ScrollFade delay={0.1} className="lg:col-span-7">
            <div className="border hairline p-6 md:p-8 bg-white">
              <div className="overline text-[#C9A227]">Or drop us a line</div>
              <h2 className="mt-2 text-2xl md:text-3xl font-light text-[#163A2F]">Send a message</h2>

              <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="overline text-[#163A2F]/70">Name</label>
                  <input
                    data-testid="contact-name" required
                    value={form.name} onChange={(e) => update("name", e.target.value)}
                    className="mt-1 w-full px-4 py-3 border hairline bg-transparent focus:outline-none focus:border-[#C9A227]"
                  />
                </div>
                <div>
                  <label className="overline text-[#163A2F]/70">Email</label>
                  <input
                    type="email" data-testid="contact-email" required
                    value={form.email} onChange={(e) => update("email", e.target.value)}
                    className="mt-1 w-full px-4 py-3 border hairline bg-transparent focus:outline-none focus:border-[#C9A227]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline text-[#163A2F]/70">Subject</label>
                  <input
                    data-testid="contact-subject"
                    value={form.subject} onChange={(e) => update("subject", e.target.value)}
                    placeholder="Order, sizing, returns, partnership…"
                    className="mt-1 w-full px-4 py-3 border hairline bg-transparent focus:outline-none focus:border-[#C9A227]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline text-[#163A2F]/70">Message</label>
                  <textarea
                    data-testid="contact-message" required rows={5}
                    value={form.message} onChange={(e) => update("message", e.target.value)}
                    className="mt-1 w-full px-4 py-3 border hairline bg-transparent focus:outline-none focus:border-[#C9A227] resize-y"
                  />
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit" disabled={sending}
                    data-testid="contact-submit"
                    className="bg-[#163A2F] text-white px-8 py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#0A1C16] transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {sending ? "Opening mail…" : (<><Send className="w-4 h-4" /> Send message</>)}
                  </button>
                  <p className="text-[11px] text-[#163A2F]/55 mt-2">This opens your email client addressed to {SUPPORT_EMAIL}. Once Resend is wired up, sending will happen instantly from the site.</p>
                </div>
              </form>
            </div>
          </ScrollFade>
        </div>
      </section>
    </Layout>
  );
}
